import { useState, useRef, useEffect } from 'react';
import { Camera, ArrowLeft, Sparkles, Lock, Image as ImageIcon, Loader2, Pencil, Barcode } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { healthDataService, CalorieEntryDto } from '../services/health-data.service';
import { aiAssistantService } from '../services/ai-assistant.service';
import { subscriptionsService } from '../services/subscriptions.service';
import { LoadingSkeleton } from './LoadingSkeleton';
import { ManualFoodEntry, FoodEntry } from './ManualFoodEntry';
import { BarcodeScanner } from './BarcodeScanner';
import toast from 'react-hot-toast';
import { handleApiError } from '../lib/api-client';

interface CalorieTrackerProps {
  onBack: () => void;
  onUpgrade: () => void;
}

export function CalorieTracker({ onBack, onUpgrade }: CalorieTrackerProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [cameraMode, setCameraMode] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  const [reviewEntries, setReviewEntries] = useState<
    Array<{ name: string; quantity?: number; calories: number; carbs?: number; protein?: number; fat?: number }>
  >([]);
  const [reviewImage, setReviewImage] = useState<string | null>(null);

  // Check subscription
  const { data: subscription } = useQuery({
    queryKey: ['subscription', user?.id],
    queryFn: () => subscriptionsService.getCurrentSubscription(),
    enabled: !!user,
    retry: false,
  });

  const hasActiveSubscription = !!subscription;

  // Fetch calorie entries
  const {
    data: calorieEntriesData,
    isLoading: entriesLoading,
    error: entriesError,
  } = useQuery({
    queryKey: ['calorie-entries', user?.id],
    queryFn: () => healthDataService.getCalorieEntries({ limit: 10 }),
    enabled: !!user,
  });

  const calorieEntries = calorieEntriesData?.data || calorieEntriesData || [];

  // Create calorie entry mutation
  const createMutation = useMutation({
    mutationFn: (data: CalorieEntryDto) => healthDataService.createCalorieEntry(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calorie-entries'] });
      toast.success('Food logged successfully!');
      setSelectedImage(null);
      if (!hasActiveSubscription) {
        setShowUpgradePrompt(true);
      }
    },
    onError: (error) => {
      toast.error(handleApiError(error));
    },
  });

  useEffect(() => {
    if (entriesError) {
      toast.error(handleApiError(entriesError));
    }
  }, [entriesError]);

  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [stream]);

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
      setCameraMode(true);
    } catch (err) {
      console.error('Error accessing camera:', err);
      toast.error('Unable to access camera. Please check permissions.');
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
    setCameraMode(false);
  };

  const capturePhoto = () => {
    if (videoRef.current) {
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(videoRef.current, 0, 0);
        const imageData = canvas.toDataURL('image/jpeg');
        setSelectedImage(imageData);
        stopCamera();
        analyzeImage(imageData);
      }
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const imageData = reader.result as string;
        setSelectedImage(imageData);
        analyzeImage(imageData);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleManualEntrySave = (
    entries: FoodEntry[],
    totalCalories: number,
    _totalCarbs: number,
    _totalProtein: number,
    _totalFat: number
  ) => {
    createMutation.mutate({
      calories: totalCalories,
      foodItems: entries.map((e) => ({
        name: e.name,
        calories: e.calories,
        carbs: e.carbs,
        protein: e.protein,
        fat: e.fat,
      })),
      imageUrl: reviewImage ?? undefined,
    });
    setShowManualEntry(false);
    setReviewEntries([]);
    setReviewImage(null);
  };

  const handleBarcodeDetected = (barcode: string, productData: Record<string, unknown>) => {
    const name = (productData.name as string) ?? 'Scanned Product';
    const servingSize = (productData.serving_size as number) ?? 100;
    const calories = (productData.calories as number) ?? 0;
    const carbs = (productData.carbs as number) ?? 0;
    const protein = (productData.protein as number) ?? 0;
    const fat = (productData.fat as number) ?? 0;
    createMutation.mutate({
      calories: Math.round(calories),
      foodItems: [{ name, calories: Math.round(calories), carbs, protein, fat }],
      notes: `Barcode: ${barcode} (${servingSize}g serving)`,
    });
    setShowBarcodeScanner(false);
  };

  const analyzeImage = async (imageData: string) => {
    try {
      setIsAnalyzing(true);
      const analysis = await aiAssistantService.analyzeFoodImage(imageData);
      const detectedItems = (analysis.foodItems || []).map((item) => ({
          name: item.name,
          calories: Number(item.calories) || 0,
          carbs: Number(item.carbs) || 0,
          protein: Number(item.protein) || 0,
          fat: Number(item.fat) || 0,
        }));

      setReviewEntries(detectedItems);
      setReviewImage(imageData);
      setShowManualEntry(true);
      setSelectedImage(null);
    } catch (err) {
      toast.error(handleApiError(err));
      setSelectedImage(null);
    } finally {
      setIsAnalyzing(false);
    }
  };

  if (entriesLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
        <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <LoadingSkeleton variant="rectangular" height="2rem" />
          </div>
        </div>
        <div className="max-w-4xl mx-auto px-6 py-8">
          <LoadingSkeleton variant="rectangular" height="200px" className="mb-8" />
          <LoadingSkeleton variant="rectangular" height="400px" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={onBack}
              className="flex items-center space-x-2 text-gray-700 hover:text-dropsugar-green transition-colors"
              aria-label="Back to Dashboard"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back to Dashboard</span>
            </button>
            <h1 className="text-xl font-bold text-gray-900">AI Plate Checker</h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="bg-gradient-to-br from-dropsugar-blue to-dropsugar-blue-dark rounded-2xl p-8 mb-8 text-white">
          <div className="flex items-start space-x-4">
            <div className="p-3 bg-white/20 backdrop-blur-md rounded-xl">
              <Sparkles className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-2">Snap a pic of your plate to check calories</h2>
              <p className="text-blue-100">
                Our AI will analyze your meal and provide detailed nutritional information
              </p>
            </div>
          </div>
        </div>

        {!cameraMode && !selectedImage && !createMutation.isPending && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <button
              type="button"
              onClick={startCamera}
              className="bg-white border-2 border-gray-200 hover:border-dropsugar-green rounded-2xl p-8 transition-all group"
              aria-label="Take Photo"
            >
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-dropsugar-green/10 group-hover:bg-dropsugar-green/20 rounded-full mb-4 transition-colors">
                  <Camera className="w-10 h-10 text-dropsugar-green" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Take Photo</h3>
                <p className="text-gray-600">Use your camera to capture a meal</p>
              </div>
            </button>

            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="bg-white border-2 border-gray-200 hover:border-dropsugar-green rounded-2xl p-8 transition-all group"
              aria-label="Upload Photo"
            >
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-green-50 group-hover:bg-green-100 rounded-full mb-4 transition-colors">
                  <ImageIcon className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Upload Photo</h3>
                <p className="text-gray-600">Choose an existing photo from your device</p>
              </div>
            </button>

            <button
              type="button"
              onClick={() => setShowManualEntry(true)}
              className="bg-white border-2 border-gray-200 hover:border-orange-500 rounded-2xl p-8 transition-all group"
              aria-label="Add Manually"
            >
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-orange-50 group-hover:bg-orange-100 rounded-full mb-4 transition-colors">
                  <Pencil className="w-10 h-10 text-orange-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Add Manually</h3>
                <p className="text-gray-600">Select from Indian food database</p>
              </div>
            </button>

            <button
              type="button"
              onClick={() => setShowBarcodeScanner(true)}
              className="bg-white border-2 border-gray-200 hover:border-purple-500 rounded-2xl p-8 transition-all group"
              aria-label="Scan Barcode"
            >
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-purple-50 group-hover:bg-purple-100 rounded-full mb-4 transition-colors">
                  <Barcode className="w-10 h-10 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Scan Barcode</h3>
                <p className="text-gray-600">Scan packaged food barcode</p>
              </div>
            </button>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>
        )}

        {cameraMode && (
          <div className="bg-white rounded-2xl overflow-hidden border-2 border-gray-200 mb-8">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              className="w-full aspect-video bg-black"
              aria-label="Camera preview"
            />
            <div className="p-6 flex justify-center space-x-4">
              <button
                onClick={capturePhoto}
                className="px-8 py-3 bg-dropsugar-green hover:bg-dropsugar-green-dark text-white font-semibold rounded-xl shadow-md transition-all"
                aria-label="Capture Photo"
              >
                Capture Photo
              </button>
              <button
                onClick={stopCamera}
                className="px-8 py-3 bg-gray-600 hover:bg-gray-700 text-white font-semibold rounded-xl shadow-md transition-all"
                aria-label="Cancel"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {(isAnalyzing || createMutation.isPending) && (
          <div className="bg-white rounded-2xl p-12 border-2 border-gray-200 mb-8">
            <div className="text-center">
              <Loader2 className="w-12 h-12 text-dropsugar-green animate-spin mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {isAnalyzing ? 'Calling AI to analyze your meal...' : 'Analyzing your meal...'}
              </h3>
              <p className="text-gray-600">
                {isAnalyzing
                  ? 'Using AI vision API to identify food and estimate calories'
                  : 'Saving your food log'}
              </p>
            </div>
          </div>
        )}

        {showUpgradePrompt && (
          <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-8 mb-8 text-white">
            <div className="flex items-start space-x-4">
              <div className="p-3 bg-white/20 backdrop-blur-md rounded-xl">
                <Lock className="w-8 h-8" />
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold mb-2">Unlock Detailed AI Analysis</h3>
                <p className="text-orange-100 mb-4">
                  Get comprehensive nutritional insights, personalized recommendations, and meal planning
                  with our premium packages
                </p>
                <button
                  onClick={onUpgrade}
                  className="px-6 py-3 bg-white text-orange-600 font-semibold rounded-xl hover:bg-orange-50 transition-all"
                >
                  View Packages
                </button>
              </div>
            </div>
          </div>
        )}

        {showManualEntry && (
          <ManualFoodEntry
            onClose={() => {
              setShowManualEntry(false);
              setReviewEntries([]);
              setReviewImage(null);
            }}
            initialEntries={reviewEntries}
            onSave={handleManualEntrySave}
          />
        )}

        {showBarcodeScanner && (
          <BarcodeScanner
            onClose={() => setShowBarcodeScanner(false)}
            onBarcodeDetected={handleBarcodeDetected}
          />
        )}

        {calorieEntries.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Recent Food Logs</h2>
            <div className="space-y-4">
              {calorieEntries.map((entry: any) => {
                const items = entry.foodItems && Array.isArray(entry.foodItems) ? entry.foodItems : [];
                const totalCarbs = items.reduce((s: number, i: any) => s + (Number(i.carbs) || 0), 0);
                const totalProtein = items.reduce((s: number, i: any) => s + (Number(i.protein) || 0), 0);
                const totalFat = items.reduce((s: number, i: any) => s + (Number(i.fat) || 0), 0);
                return (
                <div key={entry.id} className="bg-white rounded-2xl p-6 border-2 border-gray-200">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <p className="text-sm text-gray-600">
                        {new Date(entry.loggedAt || entry.date || entry.createdAt).toLocaleDateString(
                          'en-US',
                          {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          }
                        )}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold text-gray-900">{entry.calories}</p>
                      <p className="text-sm text-gray-600">calories</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="text-center">
                      <p className="text-lg font-bold text-gray-900">{totalCarbs}g</p>
                      <p className="text-xs text-gray-600">Carbs</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-gray-900">{totalProtein}g</p>
                      <p className="text-xs text-gray-600">Protein</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-gray-900">{totalFat}g</p>
                      <p className="text-xs text-gray-600">Fat</p>
                    </div>
                  </div>

                  {entry.foodItems && Array.isArray(entry.foodItems) && entry.foodItems.length > 0 && (
                    <div className="space-y-2 mb-4">
                      {entry.foodItems.map((item: any, idx: number) => {
                        return (
                          <div key={idx} className="flex items-center justify-between text-sm">
                            <span className="text-gray-700">{item.name}</span>
                            <span className="text-gray-600">{item.calories} cal</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
